import React, { useEffect, useState } from 'react'
import { Table, Button, Alert, Space, Typography, message, Card, InputNumber, Tag } from 'antd'
import { EditOutlined, SaveOutlined, LockOutlined } from '@ant-design/icons'
import apis from '../../services/Apis'
import { SecureGet, SecurePost } from '../../services/axiosCall'

const { Title, Text } = Typography

// Fixed set of years shown by default — no dynamic add/remove.
const FIXED_YEARS = [2022, 2023, 2024, 2025]

const emptyRowForYear = (year) => ({
    key: String(year),
    year,
    journal: 0,
    conference: 0,
    bookChapter: 0,
    book: 0,
    id: null,
    total_publication_count: 0,
})

// Builds exactly 4 rows (one per FIXED_YEARS entry), filling in
// data from the API where it exists and defaulting to 0 otherwise.
const mapApiToTable = (apiData = []) => {
    const byYear = new Map(apiData.map((item) => [Number(item.year), item]))

    return FIXED_YEARS.map((year) => {
        const item = byYear.get(year)
        if (!item) return emptyRowForYear(year)

        return {
            key: String(year),
            year,
            journal: item.journal_count ?? 0,
            conference: item.conference_count ?? 0,
            bookChapter: item.book_chapter_count ?? 0,
            book: item.book_count ?? 0,
            id: item.id ?? null,
            total_publication_count: item.total_publication_count ?? 0,
        }
    })
}

const mapTableToApi = (tableData = []) =>
    tableData.map(({ year, journal, conference, bookChapter, book, id, total_publication_count }) => ({
        year: String(year),
        id: id ? id : null,
        journal_count: journal,
        conference_count: conference,
        book_chapter_count: bookChapter,
        book_count: book,
        total_publication_count: total_publication_count
    }))

const PublicationDetails = () => {
    const [data, setData] = useState(FIXED_YEARS.map(emptyRowForYear))
    const [isEditing, setIsEditing] = useState(true)
    const [saved, setSaved] = useState(false)
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)

    const handleChange = (value, key, field) => {
        setData((prev) =>
            prev.map((row) => (row.key === key ? { ...row, [field]: value ?? 0 } : row))
        )
    }

    const handleSave = async () => {
        setSaving(true)
        try {
            const res = await SecurePost(
                {
                    url: apis.FACULTY_PUBLICATION_COUNT,
                    data: mapTableToApi(data)
                }

            )
            if (res?.status === 200) {
                setIsEditing(false)
                setSaved(true)
                getData()
                message.success('Publication details saved successfully')
            } else {
                message.error('Failed to save publication details')
            }
        } finally {
            setSaving(false)
        }
    }

    const handleEdit = () => {
        setIsEditing(true)
        setSaved(false)
    }

    const numberColumn = (title, field) => ({
        title,
        dataIndex: field,
        key: field,
        align: 'center',
        render: (value, record) =>
            isEditing ? (
                <InputNumber
                    min={0}
                    value={value}
                    onChange={(val) => handleChange(val, record.key, field)}
                    style={{ width: '100%' }}
                />
            ) : (
                value
            ),
    })

    const columns = [
        {
            title: 'Year',
            dataIndex: 'year',
            key: 'year',
            align: 'center',
            width: 100,
            // Year is fixed — never editable.
            render: (year) => <strong>{year}</strong>,
        },
        numberColumn('Journal', 'journal'),
        numberColumn('Conference', 'conference'),
        numberColumn('Book Chapter', 'bookChapter'),
        numberColumn('Book', 'book'),
        // numberColumn('Total Publications', 'total_publication_count'),
        {
            title: 'Total Publications',
            dataIndex: 'total_publication_count',
            key: 'total_publication_count',
            align: 'center',
        },
    ]

    const getData = async () => {
        setLoading(true)
        try {
            const res = await SecureGet({ url: apis.FACULTY_PUBLICATION_COUNT })
            if (res?.status === 200) {
                const apiData = res?.data?.results ?? []
                const tableData = mapApiToTable(apiData)
                setData(tableData)

                // If there's any real saved data among the 4 fixed years, lock the table.
                const hasSavedData = apiData.length > 0
                setIsEditing(!hasSavedData)
                setSaved(hasSavedData)
            } else {
                message.error('Failed to fetch publication details')
                setData(FIXED_YEARS.map(emptyRowForYear))
            }
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        getData()
    }, [])

    return (
        <Card
            style={{ borderRadius: 8, fontFamily: "'DM Sans','Segoe UI',sans-serif" }}
            title={<Title
                level={4}
                style={{ marginBottom: 0 }}>Publication Details</Title>}>

            <Alert
                type={saved ? 'success' : 'info'}
                showIcon
                style={{ marginBottom: 20 }}
                message={
                    <Space size={8} align="center">
                        <Text strong style={{ fontSize: 15, color: '#5c0011' }}>
                            Publication Details is Mandatory
                        </Text>
                        <Tag
                            color="error"
                            style={{
                                borderRadius: 20,
                                fontSize: 11,
                                fontWeight: 600,
                                letterSpacing: 0.3,
                                lineHeight: '18px',
                            }}
                        >
                            REQUIRED
                        </Tag>
                    </Space>
                }

                description={
                    <Text style={{ fontSize: 13.5, color: '#7a2e2e' }}>
                        This table must be fully completed before the form can be submitted.
                        Please enter the number of publications produced for{' '}
                        <Text strong style={{ color: '#5c0011' }}>each year</Text>, categorized by{' '}
                        <Text strong style={{ color: '#5c0011' }}>publication type</Text>.
                    </Text>
                }
            />
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'flex-end',
                    // marginVertical: 16,
                }}
            >
                <Space>
                    {isEditing ? (
                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            onClick={handleSave}
                            loading={saving}
                        >
                            Save
                        </Button>
                    ) : (
                        <Button icon={<EditOutlined />} onClick={handleEdit}>
                            Edit
                        </Button>
                    )}
                </Space>
            </div>

            <Table
                columns={columns}
                dataSource={data}
                pagination={false}
                loading={loading}
                rowClassName={(_, i) => (i % 2 === 0 ? 'row-even' : 'row-odd')}
            />

            {!isEditing && (
                <div style={{ marginTop: 12, color: '#999', fontSize: 13 }}>
                    <LockOutlined style={{ marginRight: 6 }} />
                    Table locked — data saved.
                </div>
            )}
        </Card>
    )
}

export default PublicationDetails